// Rate-chart ("রেট চার্ট") poster — design tokens, templates and pagination.
// Ported from the design file (Rate Chart v2.html): 6 colour themes × 6 layouts, 8 ready-made
// templates, 3 text densities, 1–2 columns, A4 / A3 / Letter. The sheet is always laid out at a
// fixed 595px width and scaled for preview, so what you see is what exports.
//
// The service list is ONE plain list (no category headings): a shop's services have no categories.

export const SHEET_W = 595;
export const STRIP_H = 8;           // accent strip on pages 2+
export const COL_GAP = 24;          // gap between the two columns
export const PAGE_LABEL_SLACK = 26; // room kept at the page bottom for "Page 1 / 2" (+ a little safety)

// Poster fonts: Anek Bangla for the big title and shop name, Hind Siliguri (already loaded app-wide) for the rest.
export const TITLE_FONT = "'Anek Bangla', 'Hind Siliguri', 'Noto Sans Bengali', sans-serif";
export const BODY_FONT = "'Hind Siliguri', 'Noto Sans Bengali', system-ui, sans-serif";

export const THEMES = [
    { id: 'teal',   hdr: '#0B4F4A', hdrInk: '#EAF5F3', acc: '#E8A33D', yearInk: '#3A2506', price: '#0B4F4A', note: '#FDF6E9' },
    { id: 'coral',  hdr: '#E4511F', hdrInk: '#FFF6F1', acc: '#1C1815', yearInk: '#FFE2D6', price: '#B5350F', note: '#FFF3EE' },
    { id: 'mono',   hdr: '#FFFFFF', hdrInk: '#1C1815', acc: '#1C1815', yearInk: '#F4F1EC', price: '#1C1815', note: '#F4F1EC' },
    { id: 'navy',   hdr: '#12244A', hdrInk: '#E7EDF8', acc: '#C9A227', yearInk: '#241A02', price: '#12244A', note: '#FBF5E4' },
    { id: 'maroon', hdr: '#7B1E23', hdrInk: '#F8ECEA', acc: '#D9C49A', yearInk: '#3A2506', price: '#7B1E23', note: '#FAF3E6' },
    { id: 'green',  hdr: '#166534', hdrInk: '#E9F6EC', acc: '#F2B23C', yearInk: '#2A1D02', price: '#166534', note: '#FDF6E9' },
];

const base = (t) => ({
    bodyBg: '#fff', pageBd: 'none', pagePad: '0',
    pageInset: 0,                       // border + padding of the page frame, both sides together (frame 24, heritage 2): the width/height it eats
    hdrTextAlign: 'left', hdrBdB: 'none', subStyle: 'normal', kickerLs: '.02em', kickerTf: 'none', badgeRadius: '999px',
    ornShow: 'none', ornJustify: 'flex-start',
    wrapBg: 'transparent', wrapBd: 'none', wrapRadius: '0', wrapPad: '0', wrapX: 0, wrapY: 0, // the card around the list
    rowRadius: '4px', rowBdB: 'none', zebra: '#F6F4F1',
    footBg: t.hdr, footInk: t.hdrInk, noteInk: '#4E3F2C',
    thBd: '1px solid #ECE9E3', thPad: '0', thBg: '#fff', thHdrH: '12px', thHdrBg: t.hdr, thHdrBd: 'none', thAlign: 'flex-start', thBandBg: t.acc, // layout thumbnails
});

export const LAYOUTS = [
    { id: 'banner', tok: (t) => ({ ...base(t), hdrPad: '20px 30px 18px', hdrBarH: '5px', titleFs: '32px', titleLs: '0' }) },
    { id: 'poster', tok: (t) => ({
        ...base(t), hdrPad: '30px 30px 26px', hdrTextAlign: 'center', hdrBarH: '8px', titleFs: '46px', titleLs: '-.01em',
        kickerLs: '.14em', kickerTf: 'uppercase', badgeRadius: '4px', ornJustify: 'center',
        rowRadius: '0', rowBdB: '1px dashed #E2DCD4', zebra: null,
        thHdrH: '18px', thAlign: 'center', thBandBg: t.hdr,
    }) },
    { id: 'minimal', tok: (t) => ({
        ...base(t), hdrPad: '22px 30px 18px', hdrBdB: '2px solid ' + t.acc, hdrBarH: '0px', titleFs: '30px', titleLs: '.12em',
        kickerLs: '.18em', kickerTf: 'uppercase', badgeRadius: '3px',
        rowRadius: '0', rowBdB: '1px solid #EEEAE4', zebra: null, noteInk: '#3F372E',
        thHdrH: '10px', thHdrBg: '#fff', thHdrBd: '1.5px solid ' + t.price, thBandBg: t.price,
    }) },
    { id: 'frame', tok: (t) => ({
        ...base(t), pageBd: '3px double ' + t.hdr, pagePad: '9px', pageInset: 24,
        hdrPad: '24px 28px 20px', hdrTextAlign: 'center', hdrBarH: '3px', titleFs: '36px', titleLs: '.02em',
        kickerLs: '.08em', badgeRadius: '4px', ornJustify: 'center',
        rowRadius: '0', zebra: '#F5F5F7',
        thBd: '2px double ' + t.hdr, thPad: '2px', thHdrH: '10px', thAlign: 'center',
    }) },
    { id: 'heritage', tok: (t) => ({
        ...base(t), bodyBg: t.note, pageBd: '1px solid ' + t.acc, pageInset: 2,
        hdrPad: '28px 30px 22px', hdrTextAlign: 'center', hdrBarH: '5px', titleFs: '38px', titleLs: '.03em', subStyle: 'italic',
        kickerLs: '.1em', ornShow: 'flex', ornJustify: 'center',
        rowRadius: '0', rowBdB: '1px dotted #D3C7B0', zebra: null,
        thBd: '1px solid ' + t.acc, thBg: t.note, thHdrH: '16px', thAlign: 'center',
    }) },
    { id: 'card', tok: (t) => ({
        ...base(t), bodyBg: '#F8F6F3', hdrPad: '20px 30px 18px', hdrBarH: '5px', titleFs: '33px', titleLs: '0',
        wrapBg: '#fff', wrapBd: '1px solid #E6E0D8', wrapRadius: '12px', wrapPad: '12px 14px', wrapX: 30, wrapY: 26,
        rowRadius: '7px', thBg: '#F4F2EE',
    }) },
];

export const DENSITIES = [
    { id: 0, nameFs: 12.5, priceFs: 13,   unitFs: 10.5, padV: 3,   padH: 8,  bodyPad: [14, 28, 10], sample: '15px' },
    { id: 1, nameFs: 14,   priceFs: 14.5, unitFs: 11.5, padV: 4.5, padH: 9,  bodyPad: [18, 30, 12], sample: '19px' },
    { id: 2, nameFs: 16,   priceFs: 16.5, unitFs: 12.5, padV: 6,   padH: 10, bodyPad: [22, 32, 14], sample: '23px' },
];

// A3 has the same proportions as A4 (it is just a bigger sheet), so the on-screen layout is shared.
// `mm` is the real sheet; Letter is a touch squarer than the 595×792 layout, so the PDF fits it inside the page.
export const PAPERS = {
    A4:     { h: 842, pdf: 'a4',     mm: [210, 297] },
    A3:     { h: 842, pdf: 'a3',     mm: [297, 420] },
    Letter: { h: 792, pdf: 'letter', mm: [215.9, 279.4] },
};

export const PRESETS = [
    { id: 't1', theme: 'teal',   layout: 'banner',   density: 1, cols: 1 },
    { id: 't2', theme: 'coral',  layout: 'poster',   density: 1, cols: 1 },
    { id: 't3', theme: 'mono',   layout: 'minimal',  density: 1, cols: 1 },
    { id: 't4', theme: 'navy',   layout: 'frame',    density: 1, cols: 1 },
    { id: 't5', theme: 'maroon', layout: 'heritage', density: 1, cols: 1 },
    { id: 't6', theme: 'green',  layout: 'card',     density: 0, cols: 2 },
    { id: 't7', theme: 'teal',   layout: 'minimal',  density: 0, cols: 2 },
    { id: 't8', theme: 'coral',  layout: 'card',     density: 1, cols: 1 },
];

export const getTheme = (id) => THEMES.find((x) => x.id === id) || THEMES[0];
export const getLayout = (id, theme) => (LAYOUTS.find((x) => x.id === id) || LAYOUTS[0]).tok(theme);

// ── number helpers ────────────────────────────────────────────────────────
const BN = '০১২৩৪৫৬৭৮৯';
export const toBnDigits = (s) => String(s).replace(/[0-9]/g, (d) => BN[d]);
export const toAsciiDigits = (s) => String(s).replace(/[০-৯]/g, (d) => String(BN.indexOf(d)));
export const localizeDigits = (s, lang) => (lang === 'en' ? toAsciiDigits(s) : toBnDigits(s));

/** A price as printed on the sheet: localized digits; a plain number gets thousands separators.
 *  Blank → '' (the caller prints "negotiable"). Free text such as "২০০–৩০০" is kept as typed. */
export function formatPrice(p, lang) {
    const text = String(p ?? '').trim();
    if (!text) return '';
    const ascii = toAsciiDigits(text);
    const n = Number(ascii);
    const shown = /^\d+(\.\d+)?$/.test(ascii) && Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : text;
    return localizeDigits(shown, lang);
}

// ── geometry ──────────────────────────────────────────────────────────────
/** Widths the sheet is laid out with — shared by the visible pages and the hidden measuring ruler. */
export function layoutMetrics(ly, d, cols) {
    const [padT, padX, padB] = d.bodyPad;
    const contentW = SHEET_W - ly.pageInset;                                  // inside the page frame
    const bodyW = contentW - 2 * padX - ly.wrapX;                             // inside the body padding (+ card)
    const colW = (bodyW - (cols - 1) * COL_GAP) / cols;
    return { padT, padX, padB, contentW, bodyW, colW };
}

/** Best split of one page's rows over `cols` columns (rows keep reading order, columns end up even). */
function balance(rows, heights, cols) {
    if (cols === 1 || rows.length < 2) return { columns: cols === 1 ? [rows.slice()] : [rows.slice(), []], max: rows.reduce((s, i) => s + heights[i], 0) };
    const total = rows.reduce((s, i) => s + heights[i], 0);
    let acc = 0, best = { at: 1, max: Infinity };
    for (let k = 1; k < rows.length; k += 1) {
        acc += heights[rows[k - 1]];
        const m = Math.max(acc, total - acc);
        if (m <= best.max) best = { at: k, max: m }; // on a tie the left column keeps the extra row
    }
    return { columns: [rows.slice(0, best.at), rows.slice(best.at)], max: best.max };
}

/**
 * Split measured row heights over pages.
 *   heights   px height of every row, in order — measured at the real column width, so wrapping is exact
 *   cols      1 or 2 (two columns are balanced on every page)
 *   capacity  (pageIndex) => px of ONE column's body on that page, footer not included
 *   footH     px the note + shop strip need on the LAST page
 * → [{ columns: number[][] }]   row indexes per column, per page (an empty list gets one empty page)
 *
 * Pages are filled first; when the footer doesn't fit under the last one, a few rows travel to a new last page so the footer
 * never stands alone (and the earlier pages stay as full as they can).
 */
const TAIL_ROWS = 3;

export function packRows({ heights, cols, capacity, footH }) {
    if (!heights.length) return [{ columns: Array.from({ length: cols }, () => []) }];

    // 1) fill column after column, page after page
    const pages = [];
    let page = { rows: [], used: new Array(cols).fill(0), col: 0 };
    heights.forEach((h, i) => {
        const cap = capacity(pages.length);
        if (page.used[page.col] + h > cap && page.rows.length) {
            if (page.col < cols - 1) page.col += 1;
            else { pages.push(page); page = { rows: [], used: new Array(cols).fill(0), col: 0 }; }
        }
        page.rows.push(i);
        page.used[page.col] += h;
    });
    pages.push(page);

    // 2) the footer sits under the last page — if it doesn't fit there, it gets a new last page with a few rows for company
    const tail = pages[pages.length - 1];
    if (balance(tail.rows, heights, cols).max + footH > capacity(pages.length - 1)) {
        let take = Math.min(TAIL_ROWS, tail.rows.length - 1); // always leave one row behind
        const room = capacity(pages.length);                   // the new page must hold those rows AND the footer
        while (take > 0 && balance(tail.rows.slice(tail.rows.length - take), heights, cols).max + footH > room) take -= 1;
        pages.push({ rows: tail.rows.splice(tail.rows.length - take, take) });
    }

    return pages.map((p) => ({ columns: balance(p.rows, heights, cols).columns }));
}
